import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTurnstile } from '../../hooks/useTurnstile';
import { ENV } from '../../config/envConfig';

interface TurnstileVerificationProps {
  onVerified: (token: string) => void;
  onError?: (error: string) => void;
  onReset?: () => void;
  siteKey?: string;
  autoReset?: boolean;
  className?: string;
}

export const TurnstileVerification: React.FC<TurnstileVerificationProps> = ({
  onVerified,
  onError,
  onReset,
  siteKey = ENV.TURNSTILE_SITE_KEY,
  autoReset = true,
  className = '',
}) => {
  const [isHumanChecked, setIsHumanChecked] = useState(false);
  const [showTurnstile, setShowTurnstile] = useState(false);

  // Preload Cloudflare logo image
  useEffect(() => {
    const img = new Image();
    img.src = "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSvaaS6cz2-k_AywDZ0twr_KFwOC5CHQPiFNA&s";
  }, []);

  const handleHumanCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setIsHumanChecked(true);
      setTimeout(() => {
        setShowTurnstile(true);
      }, 1000);
    }
  };

  const { turnstileRef, token, error: turnstileError } = useTurnstile(
    showTurnstile ? siteKey : null
  );

  // Handle token verification
  useEffect(() => {
    if (token) {
      onVerified(token);
    }
  }, [token, onVerified]);

  // Handle errors
  useEffect(() => {
    if (turnstileError) {
      onError?.(turnstileError);
      if (autoReset) {
        setIsHumanChecked(false);
        setShowTurnstile(false);
        onReset?.();
      }
    }
  }, [turnstileError, onError, autoReset, onReset]);

  return (
    <div className={`flex justify-center ${className}`}>
      <div className="w-[300px] min-h-[74px] flex items-center justify-center relative">
        {/* I am Human Checkbox */}
        {!isHumanChecked && (
          <div className="border border-[#d3d3d3] rounded-[3px] bg-[#f9f9f9] shadow-sm hover:border-gray-400 transition-all duration-200 w-full h-[74px] flex items-center justify-between px-3">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isHumanChecked}
                onChange={handleHumanCheck}
                className="w-[25px] h-[25px] text-blue-600 border-2 border-[#c3c3c3] rounded-sm cursor-pointer focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 transition-all mr-3"
                aria-label="I'm not a robot verification"
              />
              <span className="text-[14px] text-[#555555] font-normal select-none leading-tight">
                I'm not a robot
              </span>
            </label>
            <div className="flex flex-col items-center gap-1">
              <img
                src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSvaaS6cz2-k_AywDZ0twr_KFwOC5CHQPiFNA&s"
                alt="Cloudflare"
                className="h-8 w-auto"
              />
              <span className="text-[7px] text-[#9d9d9d] leading-none">Privacy - Terms</span>
            </div>
          </div>
        )}

        {/* Loading State with Circle Dots Chasing Animation */}
        <AnimatePresence>
          {isHumanChecked && !showTurnstile && !token && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="absolute inset-0 border border-[#d3d3d3] rounded-[3px] bg-[#f9f9f9] shadow-sm w-full h-[74px] flex items-center justify-between px-3"
            >
              <div className="flex items-center gap-3">
                {/* Framer Motion Rotating Circle Loader */}
                <div className="relative w-7 h-7">
                  {/* Rotating container */}
                  <motion.div
                    className="absolute inset-0"
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    {/* Static dots in circular pattern - only rotation */}
                    {[...Array(8)].map((_, i) => {
                      const angle = (i * 360) / 8;
                      const radius = 11;
                      const x = Math.cos((angle * Math.PI) / 180) * radius;
                      const y = Math.sin((angle * Math.PI) / 180) * radius;
                      
                      return (
                        <div
                          key={i}
                          className="absolute w-2 h-2 bg-green-500 rounded-full"
                          style={{
                            left: '50%',
                            top: '50%',
                            transform: `translate(${x}px, ${y}px) translate(-50%, -50%)`,
                          }}
                        />
                      );
                    })}
                  </motion.div>
                </div>
                <span className="text-[14px] text-[#555555]">Verifying...</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <img
                  src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSvaaS6cz2-k_AywDZ0twr_KFwOC5CHQPiFNA&s"
                  alt="Cloudflare"
                  className="h-8 w-auto"
                />
                <span className="text-[7px] text-[#9d9d9d] leading-none">Privacy - Terms</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Cloudflare Turnstile Widget */}
        {showTurnstile && !token && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeInOut", delay: 0.2 }}
            className="absolute inset-0 w-full flex justify-center items-center"
          >
            <div ref={turnstileRef}></div>
          </motion.div>
        )}

        {/* Success State */}
        <AnimatePresence>
          {token && (
            <motion.div
              key="success"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeInOut", delay: 0.2 }}
              className="absolute inset-0 border border-green-500 rounded-[3px] bg-green-50 shadow-sm w-full h-[74px] flex items-center justify-between px-3"
            >
            <div className="flex items-center gap-3">
              <div className="w-[25px] h-[25px] bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-[14px] text-green-700 font-medium">Verified!</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <img
                src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSvaaS6cz2-k_AywDZ0twr_KFwOC5CHQPiFNA&s"
                alt="Cloudflare"
                className="h-8 w-auto"
              />
              <span className="text-[7px] text-[#9d9d9d] leading-none">Privacy - Terms</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </div>
  );
};

export default TurnstileVerification;
